import { Paper, Stack, Typography } from '@mui/material'
import { CarrierType, CarrierVisitStatus } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { RemoveOrFinishOpenOrders } from '@planning/pages/Order'
import { getVesselTypeLabel } from '@planning/pages/Vessel/components/Molecules/VesselForm'
import { useVesselMessagesHandler } from '@planning/pages/Vessel/hooks/useVesselMessagesHandler'
import { IVesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { vesselVisitService } from '@planning/services'
import { useTranslate } from '@tolgee/react'
import { ButtonWithDropdown, EmptyIndicator, useMinimalsTheme } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { FC, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { CancelVisitDialog } from '../../molecules/CancelVisitDialog'
import { VisitDataHeaderAttributesCard } from '../../molecules/VisitDataHeaderAttributesCard'
import { VisitTimingDialog } from '../../molecules/VisitTimingDialog'
import { VesselVisitInformation } from './VesselVisitInformation'
import { VisitTimings } from './VisitTimings'

type VesselVisitOverviewHeaderProps = {
  vesselVisitItem: IVesselVisitItem
  isEditDisabled?: boolean
}

export const VesselVisitOverview: FC<VesselVisitOverviewHeaderProps> = observer(props => {
  const { vesselVisitItem, isEditDisabled = false } = props
  const theme = useMinimalsTheme()
  const { t } = useTranslate()
  const navigate = useNavigate()
  const { dialogStore, appViewStore } = usePlanningStore()
  const { getTimestampExceptionMessage } = useVesselMessagesHandler()

  const vesselVisitNumber = vesselVisitItem?.data.number
  const portCallNumber = vesselVisitItem?.data.portCallId
  const operator = vesselVisitItem?.operator?.data?.name
  const agent = vesselVisitItem?.shippingAgent?.data?.name
  const vesselService = `${vesselVisitItem?.data?.vesselServiceName}/${vesselVisitItem?.data?.vesselServiceServiceId}`
  const portRotation = vesselVisitItem?.data?.portRotation?.join(', ')
  const voyageNumber = vesselVisitItem?.voyages?.map(voyage => {
    const voyageNumber = `${voyage.inboundNumber}, ${voyage.outboundNumber}`
    const voyageShippingLine = voyage.shippingLine?.shortName

    if (voyageShippingLine) {
      return `${voyageNumber} (${voyageShippingLine})`
    }

    return voyageNumber
  })

  const isVisitExpected = vesselVisitItem.data?.status === CarrierVisitStatus.Expected
  const isVisitCancelled = vesselVisitItem.data?.status === CarrierVisitStatus.Cancelled
  const isVisitArrivedButNotDeparted =
    vesselVisitItem.data?.ata != null && vesselVisitItem.data?.atd == null

  const openSetAtdModal = useCallback(() => {
    dialogStore.openDialog(
      <VisitTimingDialog
        initialValues={{ date: dayjs(vesselVisitItem?.data.atd ?? vesselVisitItem?.data.etd) }}
        timingType='ATD'
        noCloseAfterConfirm={true}
        onSubmit={async value => {
          const atd = dayjs(value) as any
          const atb = vesselVisitItem?.data.atb ?? vesselVisitItem?.data.ata
          const operationStart = vesselVisitItem?.data.operationStart ?? atb
          const operationEnd = vesselVisitItem?.data.operationEnd ?? atd

          if (vesselVisitItem?.hasOpenOrders) {
            dialogStore.openDialog(
              <RemoveOrFinishOpenOrders
                visitItem={vesselVisitItem}
                callback={async (
                  handleOpenInboundOrdersOption,
                  handleOpenOutboundOrdersOption,
                  handleOpenRestowOrdersOption,
                ) => {
                  await vesselVisitService.put({
                    ...vesselVisitItem.data,
                    atd,
                    atb,
                    operationStart,
                    operationEnd,
                    vesselIds: vesselVisitItem.data.carrierIds,
                    handleOpenInboundOrdersOption,
                    handleOpenOutboundOrdersOption,
                    handleOpenRestowOrdersOption,
                  })
                }}
              />,
              false,
              {
                open: true,
                maxWidth: 'lg',
                slotProps: {},
              },
            )
            return
          }

          await vesselVisitService.put({
            ...vesselVisitItem.data,
            atd,
            atb,
            operationStart,
            operationEnd,
            vesselIds: vesselVisitItem.data.carrierIds,
          })
          dialogStore.close()
        }}
      />,
      false,
      {
        open: true,
        maxWidth: 'xs',
        slotProps: {},
      },
    )
  }, [dialogStore, vesselVisitItem])

  const getTimeStampName = (status: CarrierVisitStatus) => {
    switch (status) {
      case CarrierVisitStatus.Departed:
        return 'ATD'
      case CarrierVisitStatus.Completed:
        return 'ATC'
      case CarrierVisitStatus.InOperation:
        return 'ATO'
      case CarrierVisitStatus.Arrived:
        return vesselVisitItem.data?.atb ? 'ATB' : 'ATA'
      default:
        return ''
    }
  }

  const onRevertStatus = async () => {
    const { status, number, id } = vesselVisitItem.data ?? {}

    let title = ''
    let description = ''
    let primaryActionText = ''
    let secondaryActionText = ''
    const timeStampName = getTimeStampName(status)

    if (status === CarrierVisitStatus.Closed) {
      title = t('revertToDeparted', 'Revert visit {number} to "Departed"?', { number })
      description = t(
        'vesselVisitDepartedAndWillBeEditable',
        'The vessel visit will be set to "Departed" and you will be able to edit it.',
      )
      primaryActionText = t('yesRevert', 'Yes, revert')
      secondaryActionText = t('noKeepStatus', 'No, keep status')
    } else {
      title = t('removeTimeStamp', 'Remove the {name} for the vessel {vesselPortCallId}?', {
        name: timeStampName,
        vesselPortCallId: number,
      })
      description = t(
        'ordersAndStatusWillRemainUnchanged',
        'The orders and their statuses will remain unchanged.',
      )
      primaryActionText = t('yesRemove', 'Yes, remove')
      secondaryActionText = t('noKeepTimeStamp', 'No, keep timestamp')
    }

    const isConfirmed = await appViewStore.setOpenConfirmDialog(
      true,
      <Stack>
        <Typography>{description}</Typography>
      </Stack>,
      title,
      '',
      {
        primaryActionText,
        secondaryActionText,
      },
    )
    if (isConfirmed && id) {
      try {
        await vesselVisitService.revertCurrentVesselVisitStatus({
          id,
          carrierType: CarrierType.Vessel,
        })
        if (status === CarrierVisitStatus.Closed) {
          appViewStore.setShowAlert(
            'success',
            t(
              'visitStatusRevertedToDeparted',
              'The visit status was successfully reverted to "Departed".',
            ),
          )
        } else {
          appViewStore.setShowAlert(
            'success',
            t('timestampSuccessFullyRemoved', '{name} was successfully removed.', {
              name: timeStampName,
            }),
          )
        }
      } catch (error) {
        const visitOnTerminalMessage = getTimestampExceptionMessage(error)
        if (visitOnTerminalMessage) {
          appViewStore.setShowAlert('error', visitOnTerminalMessage)
        } else {
          appViewStore.setShowAlert('error', t('failedToRemove/Revert', 'Failed to remove/revert.'))
        }
      }
    }
  }

  const openCancelVisitDialog = useCallback(() => {
    dialogStore.openDialog(
      <CancelVisitDialog
        identifier={vesselVisitNumber}
        onSubmit={async () => {
          await vesselVisitService.cancelVisit({ id: vesselVisitItem.data.id })
          appViewStore.setShowAlert('success', t('visitCancelled', 'Visit cancelled successfully'))
          dialogStore.close()
        }}
      />,
      false,
      {
        open: true,
        maxWidth: 'xs',
        slotProps: {},
      },
    )
  }, [dialogStore, vesselVisitNumber, vesselVisitItem.data.id, appViewStore, t])

  const getRevertStatusLabel = useCallback(() => {
    let label = ''
    if (vesselVisitItem.data?.status === CarrierVisitStatus.Closed) {
      label = t('revertToDeparted', 'Revert to "Departed"')
    } else if (vesselVisitItem.data?.status === CarrierVisitStatus.Departed) {
      label = t('removeAtd', 'Remove ATD')
    } else if (vesselVisitItem.data?.status === CarrierVisitStatus.Completed) {
      label = t('removeAtc', 'Remove ATC')
    } else if (vesselVisitItem.data?.status === CarrierVisitStatus.InOperation) {
      label = t('removeAto', 'Remove ATO')
    } else if (
      vesselVisitItem.data?.status === CarrierVisitStatus.Arrived &&
      vesselVisitItem.data?.atb
    ) {
      label = t('removeAtb', 'Remove ATB')
    } else if (vesselVisitItem.data?.status === CarrierVisitStatus.Arrived) {
      label = t('removeAta', 'Remove ATA')
    } else {
      label = ''
    }
    return label
  }, [t, vesselVisitItem.data?.status, vesselVisitItem.data?.atb])

  const getVisitOptions = () => {
    const options = []

    options.push({
      label: t('editVisit', 'Edit visit'),
      testId: 'edit-visit-menu-item',
      disabled: isVisitCancelled,
      onClick: () =>
        navigate(`${PATHS.visits.vesselPlanning.url}/${vesselVisitItem.id}`, {
          state: { from: 'dashboard' },
        }),
    })

    if (isVisitArrivedButNotDeparted) {
      options.push({
        label: t('departVisit', 'Depart visit'),
        testId: 'depart-visit-menu-item',
        onClick: openSetAtdModal,
      })
    }

    if (isVisitCancelled) {
      options.push({
        label: t('reactivateVisit', 'Reactivate visit'),
        onClick: async () => {
          await vesselVisitService.reactivateVisit({
            id: vesselVisitItem.data.id,
          })
          appViewStore.setShowAlert(
            'success',
            t('visitReactivated', 'Visit reactivated successfully'),
          )
        },
      })
    } else {
      options.push({
        label: t('cancelVisit', 'Cancel visit'),
        disabled: !isVisitExpected || vesselVisitItem.orders.length > 0,
        onClick: openCancelVisitDialog,
        testId: 'cancel-visit-menu-item',
      })
    }

    if (!isVisitExpected) {
      options.push({
        label: getRevertStatusLabel(),
        onClick: onRevertStatus,
        testId: 'rever-current-status',
      })
    }

    return options
  }

  return (
    <Paper>
      <Stack
        direction='column'
        sx={{ paddingX: theme.customSpacing.l, paddingY: theme.customSpacing.m }}
      >
        <Stack direction='row' alignItems='center' spacing={theme.customSpacing.xs}>
          <Typography variant='h4'>{t('visitDetails', 'Visit details')}</Typography>
        </Stack>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={theme.customSpacing.s}
          justifyContent={'space-between'}
        >
          <Stack>
            <VesselVisitInformation
              vesselVisitNumber={vesselVisitNumber}
              portCallNumber={portCallNumber}
              voyageNumber={voyageNumber}
              operator={operator}
              agent={agent}
              vesselService={vesselService}
              portRotation={portRotation}
              status={vesselVisitItem?.data?.status}
              isBerthed={vesselVisitItem?.isBerthed}
            />
          </Stack>
          <Stack>
            <Stack direction='column' spacing={theme.customSpacing.s}>
              <Stack
                direction={'row'}
                justifyContent={'space-between'}
                spacing={theme.customSpacing.s}
              >
                <Stack
                  direction={{ xs: 'column', lg: 'row' }}
                  spacing={theme.customSpacing.s}
                  justifyContent={'flex-start'}
                >
                  <VisitDataHeaderAttributesCard
                    title={t('vessel', 'Vessel')}
                    width='400px'
                    attributes={[
                      {
                        label: t('type', 'Type'),
                        value: getVesselTypeLabel(vesselVisitItem?.vessels[0]?.data.vesselType, t),
                      },
                      {
                        label: t('lengthM', 'Length (m)'),
                        value: vesselVisitItem?.vessels[0]?.data.length,
                      },
                      {
                        label: t('draftInM', 'Draft in (m)'),
                        value: vesselVisitItem?.data?.importDraft,
                      },
                      {
                        label: t('draftOutM', 'Draft out (m)'),
                        value: vesselVisitItem?.data?.exportDraft,
                      },
                    ]}
                  />
                  <VisitDataHeaderAttributesCard
                    width='275px'
                    title={t('berth', 'Berth')}
                    warningText={
                      vesselVisitItem.data.isConflicted || vesselVisitItem.hasDraftConflict
                        ? t('editVisitToResolveConflict', 'Edit visit to resolve conflict')
                        : undefined
                    }
                    attributes={[
                      {
                        label: t('qmmM', 'QMM (m)'),
                        value: t('XtoY', '{from} to {to}', {
                          from: vesselVisitItem?.data.qmmFrom ?? EmptyIndicator,
                          to: vesselVisitItem?.data.qmmTo ?? EmptyIndicator,
                        }),
                      },
                      {
                        label: t('berth', 'Berth'),
                        maxWidth: '62px',
                        value: vesselVisitItem?.berths.map(b => b.name).join(', '),
                      },
                      {
                        label: t('dockingSide', 'Docking side'),
                        value: t('berthSide', '{berthSide} side', {
                          berthSide: vesselVisitItem?.data.berthSide,
                        }),
                      },
                    ]}
                  />
                  <VisitDataHeaderAttributesCard
                    width='275px'
                    title={t('estimates', 'Estimates')}
                    attributes={[
                      {
                        label: t('discharge', 'Discharge'),
                        value: vesselVisitItem?.data?.dischargeEstimate ?? 0,
                      },
                      {
                        label: t('load', 'Load'),
                        value: vesselVisitItem?.data?.loadEstimate ?? 0,
                      },
                      {
                        label: t('restow', 'Restow'),
                        value: vesselVisitItem?.data?.restowEstimate ?? 0,
                      },
                    ]}
                  />
                </Stack>

                {!isEditDisabled && (
                  <Stack direction={'row'}>
                    <ButtonWithDropdown
                      variant='contained'
                      color='inherit'
                      testId='edit-visit-btn'
                      options={getVisitOptions()}
                      label={t('visit', 'Visit')}
                      sx={{ width: '130px !important', height: '100% !important' }}
                    />
                  </Stack>
                )}
              </Stack>

              <Stack
                direction={'row'}
                justifyContent={'space-between'}
                spacing={theme.customSpacing.s}
              >
                <VisitTimings vesselVisitItem={vesselVisitItem} isEditDisabled={isEditDisabled} />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  )
})
